import axios from "axios";
import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import messages from "../../../../errors/messages.en-IN.json";
import useAuthStore from "../../../../stores/useAuthStore";
import { ccavenueBaseUrl } from "../../../../lib/paymentHubUrls";
import { trace } from "../../../../utils/debugTrace";

const getProviderConfig = (providerConfigurations, key) => {
  const config = providerConfigurations?.find((c) => c.configKey === key);
  return config?.configValue || "";
};

export const processCCAvenuePayment = async (
  amountAdded,
  currentPaymentMethod,
  paymentMethod,
  value,
  cart,
  setCart,
  setLoader,
  setPaymentModal,
  completePayment,
  setSelectedPaymentMethod,
  setAmount,
  setLoading,
  dispatchCCAvenueEvent,
  closeCCAvenueModal,
  setLayoutType,
  setKioskUI
) => {
  const tillData = useAuthStore.getState().tillData;
  const providerConfigurations = paymentMethod?.paymentProviderDetails?.providerConfigurations;
  const merchantId = getProviderConfig(providerConfigurations, "MerchantID");
  const accessCode = getProviderConfig(providerConfigurations, "AccessCode");

  if (!merchantId || !accessCode) {
    message.error(`${messages.errors.payments.configuration_payment_failed}`);
    return;
  }

  const API_URL = ccavenueBaseUrl();
  const trxId = uuidv4().replace(/-/g, "").toUpperCase();
  const orderId = cart.documentno || trxId;

  // BUG-1786 diagnostics: back-office URL opening on customer's mobile after
  // CCAvenue payment. Non-sensitive provider config (Host is the suspect —
  // if the backend derives the CCAvenue return_url from it, a misconfigured
  // Host in this payment method's Back Office setup would explain it).
  trace("CCAvenue", "initiate: provider config", {
    apiUrl: API_URL,
    orderId,
    host: getProviderConfig(providerConfigurations, "Host"),
    merchantIdSet: !!merchantId,
    accessCodeSet: !!accessCode,
    configKeys: providerConfigurations?.map((c) => c.configKey),
  });
  const currency = tillData?.tillAccess?.csBunit?.currencies?.[0]?.isoCode || "INR";
  const customer = cart.customer || {};
  const bunitAddress = tillData?.tillAccess?.csBunit?.customerAddress || {};

  setLoader(true);
  setPaymentModal(false);

  try {
    const paymentData = {
      order_id: orderId,
      amount: String(amountAdded),
      currency,
      billing_name: customer.name || "",
      billing_email: customer.email || "",
      billing_tel: customer.mobileNo || "",
      billing_address: customer.address || bunitAddress.line1 || "",
      billing_city: customer.city || bunitAddress.city || "",
      billing_state: customer.state || bunitAddress.region || "",
      billing_zip: customer.pincode || bunitAddress.postalCode || "",
      billing_country: customer.country || bunitAddress.country || "India",
      merchant_param1: orderId,
      merchant_param2: tillData?.tillAccess?.cwrTill?.till || "",
      merchant_param3: tillData?.tillAccess?.cwrTillID || "",
      merchant_param4: tillData?.tillAccess?.csUserId || "",
      language: "EN",
    };

    const initiateResponse = await axios.post(`${API_URL}/initiate`, {
      paymentData,
      providerConfigurations,
    });

    const { transactionUrl, order_id: serverOrderId } = initiateResponse.data;

    trace("CCAvenue", "initiate: response", {
      orderId: serverOrderId || orderId,
      transactionUrl,
      transactionUrlOrigin: (() => {
        try {
          return new URL(transactionUrl).origin;
        } catch {
          return null;
        }
      })(),
    });

    if (!transactionUrl) {
      throw new Error("No transaction URL returned from server");
    }

    const paymentResponse = await new Promise((resolve) => {
      dispatchCCAvenueEvent(
        { transactionUrl, orderId: serverOrderId || orderId, amount: amountAdded },
        paymentMethod,
        API_URL,
        providerConfigurations,
        (eventData) => {
          resolve(eventData);
        }
      );
    });

    setLoader(false);

    if (paymentResponse?.order_status === "Success") {
      completePayment(paymentMethod, value, cart, setCart, setAmount, setSelectedPaymentMethod, 0, setLayoutType, setKioskUI);
    } else {
      setSelectedPaymentMethod({});
      setKioskUI(5);
      const customEvent = new CustomEvent("customStorageChange", {
        detail: { key: "kioskUI", newValue: 5 },
      });
      window.dispatchEvent(customEvent);
      localStorage.setItem("kioskUI", 5);
      setLayoutType(0);
      localStorage.setItem("layoutType", 0);
      const layoutEvent = new CustomEvent("customStorageChange", {
        detail: { key: "layoutType", newValue: 0 },
      });
      window.dispatchEvent(layoutEvent);
    }
  } catch (error) {
    setLoader(false);
    message.error(`${messages.info.payment_failed}: ${error.message || messages.info.qr_generation_failed}`);
    console.error("CC Avenue payment error:", error);
  }
};
